import { RoleId } from '../../../insights/types/RoleTypes';
import { Application, FunctionalArea, DataLeadWithFullName } from '../types';
import {
  ASSIGNMENT_MODES,
  type AssignmentMode,
} from '../constants/assignmentModes';

/**
 * Page size for application fetching (number of applications to fetch per functional area)
 */
export const APPLICATION_PAGE_SIZE = 100;

/**
 * Normalize login string for comparison
 * @param login - The login string to normalize
 */
export const normalizeLogin = (login?: string): string => {
  return login?.toLowerCase().trim() || '';
};

/**
 * Get enhanced data leads with fullName computed for a functional area
 * @param functionalAreas - The functional areas data
 * @param selectedFunctionalArea - The selected functional area
 */
export const getEnhancedDataLeads = (
  functionalAreas: FunctionalArea[] | undefined,
  selectedFunctionalArea: string,
): DataLeadWithFullName[] => {
  const selectedFunctionalAreaData = functionalAreas?.find(
    (fa: FunctionalArea) => fa.name === selectedFunctionalArea,
  );
  return (selectedFunctionalAreaData?.dataLeads || []).map(lead => ({
    ...lead,
    fullName: `${lead.firstName} ${lead.lastName}`,
  }));
};

/**
 * Get data lead full name by login (case-insensitive)
 * @param leadLogin - The login of the data lead
 * @param functionalAreas - The functional areas data
 * @param selectedFunctionalArea - The selected functional area
 */
export const getDataLeadFullName = (
  leadLogin: string,
  functionalAreas: FunctionalArea[] | undefined,
  selectedFunctionalArea: string,
): string => {
  const enhancedDataLeads = getEnhancedDataLeads(
    functionalAreas,
    selectedFunctionalArea,
  );
  const lead = enhancedDataLeads.find(
    l => normalizeLogin(l.login) === normalizeLogin(leadLogin),
  );
  return lead?.fullName || leadLogin;
};

/**
 * Format application count with "+" when there might be more pages
 * @param count - The current application count
 * @param isLoadingMore - Flag indicating if more applications are being loaded
 * @param functionalAreaName - The name of the functional area
 * @param completeFunctionalAreasData - The complete functional areas data
 * @param selectedFunctionalArea - The selected functional area
 */
export const formatApplicationCount = (
  count: number,
  isLoadingMore: boolean = false,
  functionalAreaName?: string,
  completeFunctionalAreasData: FunctionalArea[] = [],
  selectedFunctionalArea?: string,
) => {
  // If count is a multiple of APPLICATION_PAGE_SIZE and we're not currently loading more,
  // and the count is greater than 0, show "+" to indicate there might be more
  if (count > 0 && count % APPLICATION_PAGE_SIZE === 0 && !isLoadingMore) {
    // Find the specific functional area data to check if we've loaded all pages
    const targetFunctionalAreaName =
      functionalAreaName || selectedFunctionalArea;
    const functionalAreaData = completeFunctionalAreasData.find(
      (fa: FunctionalArea) => fa.name === targetFunctionalAreaName,
    );
    const actualCount = functionalAreaData?.applications?.length || 0;

    // If the actual count in completeFunctionalAreasData is greater than the display count,
    // we know we have more data already loaded, so don't show "+"
    if (actualCount > count) {
      return count.toString();
    }

    // If we're showing a round number that's a multiple of APPLICATION_PAGE_SIZE,
    // show "+" to indicate potential for more pages to be fetched
    return `${count}+`;
  }
  return count.toString();
};

/**
 * Get all current data leads from application responsibilities
 * @param app - The application to get data leads from
 * @returns An array of current data leads
 */
export const getCurrentDataLeads = (app: Application) => {
  if (!app.roles) return [];

  const dataLeads: Array<{
    firstName?: string;
    lastName?: string;
    login?: string;
    email?: string;
    accountEnabled?: boolean;
  }> = [];

  // Search through all roles for data lead responsibilities
  for (const role of app.roles) {
    if (role.responsibilities) {
      const dataLeadResponsibilities = role.responsibilities.filter(
        responsibility => responsibility.roleId === RoleId.DataLead,
      );

      dataLeadResponsibilities.forEach(responsibility => {
        if (responsibility.responsibleParty) {
          dataLeads.push(responsibility.responsibleParty);
        }
      });
    }
  }

  return dataLeads;
};

/**
 * Get effective data leads for an application based on Default/Custom state logic
 *
 * Default State: When no explicit data lead responsibilities exist (roleId: 205),
 * all functional area data leads are considered assigned.
 *
 * Custom State: When explicit data lead responsibilities exist,
 * only those explicitly assigned data leads are considered assigned.
 * Uses case-insensitive comparison for login matching.
 *
 * @param app - The application to check
 * @param functionalAreaDataLeads - Array of functional area data leads
 */
export const getEffectiveDataLeads = (
  app: Application,
  functionalAreaDataLeads: DataLeadWithFullName[],
): string[] => {
  const explicitDataLeads = getCurrentDataLeads(app);

  if (explicitDataLeads.length === 0) {
    // Default State: No explicit assignments, return all functional area data leads
    return functionalAreaDataLeads
      .filter(lead => lead.login && lead.accountEnabled)
      .map(lead => lead.login!);
  }

  // Custom State: Explicit assignments exist, return only those that match functional area data leads
  const explicitLogins = explicitDataLeads
    .filter(lead => lead.login)
    .map(lead => normalizeLogin(lead.login));

  return functionalAreaDataLeads
    .filter(
      lead =>
        lead.login &&
        lead.accountEnabled &&
        explicitLogins.includes(normalizeLogin(lead.login)),
    )
    .map(lead => lead.login!);
};

/**
 * Determine if an application is using Default or Custom assignment mode
 * @param app - The application to check
 */
export const getDataLeadMode = (app: Application): AssignmentMode => {
  const explicitDataLeads = getCurrentDataLeads(app);
  return explicitDataLeads.length === 0
    ? ASSIGNMENT_MODES.DEFAULT
    : ASSIGNMENT_MODES.CUSTOM;
};
